import { useTranslation } from "react-i18next";
import { CheckSquare2 } from "lucide-react";
import { EmptyState } from "@/components/ui/EmptyState";
import { TasksGrid } from "./TasksGrid";
import { TasksList } from "./TasksList";
import { TasksKanbanView } from "./kanban/TasksKanbanView";
import { TasksViewOptions } from "./TasksViewOptions";
import { TasksSkeleton } from "./TasksSkeleton";
import { useTasksFilter } from "@/contexts/TasksFilterContext";
import { useTasksFiltering } from "@/hooks/tasks/useTasksFiltering";
import { Task } from "@/hooks/tasks/useTasks";
import { useAuth } from "@/providers/AuthProvider";

interface TasksContentProps {
	tasks: Task[];
	isLoading: boolean;
	onTaskUpdated?: () => void;
}

export const TasksContent = ({ tasks, isLoading, onTaskUpdated }: TasksContentProps) => {
	const { t } = useTranslation();
	const { user } = useAuth();
	const { viewMode, searchQuery, statusFilter, priorityFilter, maxDaysAgo, showCancelled, ownershipFilter, sortField, sortOrder } = useTasksFilter();

	// Apply filters to tasks
	const { filteredTasks, totalCount } = useTasksFiltering(
		tasks,
		searchQuery,
		statusFilter,
		priorityFilter,
		maxDaysAgo,
		showCancelled,
		ownershipFilter,
		user?.id || null
	);

	// Apply sorting
	const sortedTasks = [...filteredTasks].sort((a, b) => {
		let comparison = 0;
		switch (sortField) {
			case "name":
				comparison = (a.name || "").localeCompare(b.name || "");
				break;
			case "dateCreated":
				comparison = new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime();
				break;
			case "dueDate":
				if (!a.due_date && !b.due_date) comparison = 0;
				else if (!a.due_date) comparison = 1;
				else if (!b.due_date) comparison = -1;
				else comparison = new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
				break;
			case "priority": {
				const priorityOrder = { urgent: 0, high: 1, medium: 2, low: 3 };
				comparison = (priorityOrder[a.priority as keyof typeof priorityOrder] ?? 4) - (priorityOrder[b.priority as keyof typeof priorityOrder] ?? 4);
				break;
			}
		}
		return sortOrder === "asc" ? comparison : -comparison;
	});

	if (isLoading)
		return <TasksSkeleton />;

	const renderContent = () => {
		if (tasks.length === 0)
			return (
				<EmptyState
					icon={CheckSquare2}
					title={t("tasks.empty", "No tasks found")}
					description={t("tasks.empty.description", "Get started by creating your first task")}
				/>
			);

		if (filteredTasks.length === 0 && (searchQuery || statusFilter.length > 0 || priorityFilter.length > 0 || maxDaysAgo !== null))
			return (
				<EmptyState
					icon={CheckSquare2}
					title={t("tasks.search.noResults", "No tasks found")}
					description={t("tasks.search.noResultsDesc", "Try adjusting your search terms or filters")}
				/>
			);

		if (viewMode === "card")
			return <TasksGrid tasks={sortedTasks} onTaskUpdated={onTaskUpdated} />;

		if (viewMode === "kanban")
			return <TasksKanbanView tasks={sortedTasks} onTaskUpdated={onTaskUpdated} />;

		return <TasksList tasks={sortedTasks} onTaskUpdated={onTaskUpdated} />;
	};

	return (
		<div className="flex flex-col h-full">
			<TasksViewOptions />
			<div className="flex-1 min-h-0">
				{renderContent()}
			</div>
		</div>
	);
};
